/**
 * HeroCropModal (Enhancement 6).
 *
 * Given a raw image (File or object URL), opens a modal with an aspect-locked
 * crop tool. On accept, produces a cropped Blob (JPEG) which the caller can
 * POST to /api/media/upload.
 *
 * Props:
 *   open        - boolean
 *   onClose     - () => void
 *   imageSrc    - string (data URL or blob URL of the source image)
 *   aspect      - number, e.g. 16/9 (default), 21/9, 1/1
 *   title       - modal title (default "Crop image")
 *   onCropped   - (blob: Blob, previewUrl: string) => void
 */
import React, { useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Check, X } from 'lucide-react';

/**
 * Crop the given source image to the given pixel-area, returning a JPEG blob.
 * Uses a Canvas to render the crop.
 */
async function getCroppedBlob(imageSrc, area) {
  const img = await new Promise((resolve, reject) => {
    const im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = imageSrc;
  });
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(area.width);
  canvas.height = Math.round(area.height);
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    img,
    Math.round(area.x), Math.round(area.y), Math.round(area.width), Math.round(area.height),
    0, 0, Math.round(area.width), Math.round(area.height)
  );
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92);
  });
}

const HeroCropModal = ({
  open,
  onClose,
  imageSrc,
  aspect = 16 / 9,
  title = 'Crop image',
  onCropped,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_, areaPixels) => {
    setPixels(areaPixels);
  }, []);

  const finish = async () => {
    if (!pixels || !imageSrc) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(imageSrc, pixels);
      const previewUrl = URL.createObjectURL(blob);
      onCropped?.(blob, previewUrl);
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="hero-crop-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="relative w-full h-[360px] bg-neutral-900 rounded-md overflow-hidden">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              cropShape="rect"
              showGrid
            />
          )}
        </div>
        <div className="flex items-center gap-3 pt-2">
          <div className="text-xs text-neutral-600 w-14 shrink-0">Zoom</div>
          <Slider
            value={[zoom]}
            min={1}
            max={4}
            step={0.05}
            onValueChange={(v) => setZoom(v[0])}
            className="flex-1"
            data-testid="crop-zoom-slider"
          />
          <span className="text-xs text-neutral-500 w-10 text-right">{zoom.toFixed(2)}x</span>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="crop-cancel">
            <X className="h-4 w-4 mr-1.5" /> Cancel
          </Button>
          <Button
            onClick={finish}
            disabled={!pixels || busy}
            className="bg-primary-800 hover:bg-primary-900 text-white"
            data-testid="crop-accept"
          >
            <Check className="h-4 w-4 mr-1.5" /> {busy ? 'Cropping…' : 'Use this crop'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HeroCropModal;
